/**
 * PKCE (RFC 7636) + OAuth state helpers for the Cognito hosted-UI
 * authorization-code flow. Uses Web Crypto only — no dependencies.
 */

const UNRESERVED = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

/** base64url without padding (RFC 4648 §5). */
export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** High-entropy code verifier: 43..128 chars from the unreserved set. */
export function generateCodeVerifier(length = 64): string {
  if (length < 43 || length > 128) {
    throw new RangeError('PKCE code verifier length must be between 43 and 128');
  }
  // Rejection sampling keeps the distribution uniform (256 % 66 != 0).
  const out: string[] = [];
  const limit = 256 - (256 % UNRESERVED.length);
  while (out.length < length) {
    for (const b of randomBytes(length)) {
      if (b < limit && out.length < length) out.push(UNRESERVED[b % UNRESERVED.length]);
    }
  }
  return out.join('');
}

/** S256 code challenge: base64url(SHA-256(ascii(verifier))). */
export async function codeChallengeS256(verifier: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}

/** Opaque anti-CSRF state value. */
export function generateState(): string {
  return base64UrlEncode(randomBytes(32));
}
